/**
 * @fileoverview Where an order was placed (`orders."orderSourceType"`) — its vocabulary, declared once
 * (REGISTER-WEB-ORDERS-001 A3; owner rulings Q-RWO-0..4, 2026-10-22).
 *
 * ## Why it lives here
 *
 * The `orders` table is shared: the ParcelPuffin register (pos-api) and the PurchasePuffin storefront, partner API,
 * staff admin and quote conversion (purchase-api) all write it. The source was declared only in pos-api's
 * `constants/orderSources.js` and hand-copied as `'pos'` in pos-web's sale submission, and purchase-api never wrote it
 * at all — so every web, partner, staff and quote order took the column default `'pos'` and read as a register sale.
 * Both APIs and the register screen read the source from here.
 *
 * ## The sources, and who writes each
 *
 * - `pos` — rung at a register (pos-api's sale route). The register-sale gate applies to this source alone.
 * - `mail`, `intake`, `phone` — taken by staff away from the register and rung through pos-api.
 * - `web` — placed by a shopper at the storefront checkout (purchase-api).
 * - `api` — placed by a white-label partner through the partner API (purchase-api).
 * - `admin` — created by staff in PurchasePuffin's admin (purchase-api; owner ruling Q-RWO-4).
 * - `quote` — converted from an accepted quote (purchase-api; owner ruling Q-RWO-4).
 *
 * There is deliberately NO default: every writer states its source, so a writer that forgets fails loudly instead of
 * recording a register sale that never happened (Gen-0; Q-RWO-4).
 *
 * ORD1's ONE order status vocabulary (lifecycle, payment and fulfilment; MUTATION-INSTRUMENT-001 Q-ORD1-1, now built by
 * REGISTER-WEB-ORDERS-001 B1) is this module's later neighbour.
 *
 * @module @puffin-core/status-taxonomy/orderSource
 */

'use strict';

/**
 * Where an order was placed.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_SOURCE = Object.freeze({
  POS: 'pos',
  MAIL: 'mail',
  INTAKE: 'intake',
  PHONE: 'phone',
  WEB: 'web',
  API: 'api',
  ADMIN: 'admin',
  QUOTE: 'quote',
});

/**
 * Every source, in declaration order — what a validator admits and the column's CHECK names.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_SOURCE_VALUES = Object.freeze(Object.values(ORDER_SOURCE));

/**
 * The sources PurchasePuffin writes: its checkout, partner API, admin and quote conversion (REGISTER-WEB-ORDERS-001
 * A4). The register's and PurchasePuffin's orders share ONE line table (`order_line_items`, owner ruling Q-RWO-1), so a
 * reader that means "PurchasePuffin's orders" names them by source — never by which table their lines sit in.
 *
 * @type {ReadonlyArray<string>}
 */
const PURCHASE_ORDER_SOURCES = Object.freeze([
  ORDER_SOURCE.WEB,
  ORDER_SOURCE.API,
  ORDER_SOURCE.ADMIN,
  ORDER_SOURCE.QUOTE,
]);

module.exports = { ORDER_SOURCE, ORDER_SOURCE_VALUES, PURCHASE_ORDER_SOURCES };
